import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useCart } from './components/chooseride/CartContext';
import SEO from './components/SEO';
import './Checkout.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import FoodNavbar from './components/navbar/FoodNavbar';
import GiftOrderForm from './GiftOrderForm';
import { db } from './firebase';
import { ref, get } from 'firebase/database';

function Checkout() {
  const { cart, clearVendorCart } = useCart();
  const { riderData } = useAuth();
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const navigate = useNavigate();
  const location = useLocation();

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  
  const [currentLocationAddress, setCurrentLocationAddress] = useState('');
  const [note, setNote] = useState('');
  const [tempNote, setTempNote] = useState(note);

  const [isGift, setIsGift] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [specialMessage, setSpecialMessage] = useState('');
  const [tempGiftDetails, setTempGiftDetails] = useState({});

  // State for brown bag feature
  const [isBrownBagOpen, setIsBrownBagOpen] = useState(false);
  const [brownBagQuantity, setBrownBagQuantity] = useState(0);
  const brownBagPrice = 200;

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  const [newAddressInput, setNewAddressInput] = useState('');
  const [predictions, setPredictions] = useState([]);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);

  // Get vendorId and vendorType robustly from location state or session storage
  const vendorId = location.state?.vendorId || sessionStorage.getItem('9troride_checkoutVendorId');
  const vendorType = location.state?.vendorType || sessionStorage.getItem('9troride_checkoutVendorType');

  const [vendorDetails, setVendorDetails] = useState({
    name: location.state?.vendorName || '',
    image: location.state?.vendorImage || '',
  });
  const [isLoadingVendor, setIsLoadingVendor] = useState(true);

  // Filter cart for the specific vendor and calculate totals
  const vendorCart = useMemo(() => (vendorId ? cart.filter(item => item.vendorId === vendorId) : []), [cart, vendorId]);
  const cartTotal = vendorCart.reduce((sum, item) => sum + (item.totalNumericPrice * item.quantity), 0);
  const totalItemsInCart = vendorCart.reduce((sum, item) => sum + item.quantity, 0);

  const address = localStorage.getItem('9troride_address') || '';
  const locationType = localStorage.getItem('9troride_locationType') || '';

  const [deliveryAddress, setDeliveryAddress] = useState(
    riderData?.savedLocations?.home?.address || 'No saved address found.'
  );
  const deliveryFee = 500; // Example delivery fee
  const serviceFee = 850;
  const brownBagTotal = brownBagQuantity * brownBagPrice;
  const total = cartTotal + deliveryFee + serviceFee + brownBagTotal;

  useEffect(() => {
    if (!vendorId) {
      setIsLoadingVendor(false);
      console.error("No vendor ID found for checkout.");
      // Optional: navigate to an error page or back to safety
      navigate('/food-vendors', { replace: true });
      return;
    }

    const fetchVendorDetails = async () => {
      // 1. Try to get from a cart item first, as it's already loaded
      if (vendorCart.length > 0 && vendorCart[0].vendor) {
        const v = vendorCart[0].vendor;
        setVendorDetails({ name: v.name, image: v.imageUrl });
        setIsLoadingVendor(false);
        return;
      }

      // 2. If not in cart (e.g., cart is empty), fetch from DB as a fallback
      setIsLoadingVendor(true);
      
      // Determine the database path based on vendorType
      let dbPath;
      if (vendorType === 'chef') {
        dbPath = `chefs/${vendorId}`;
      } else {
        // Stores and Restaurants are both in the 'vendors' node
        dbPath = `vendors/${vendorId}`;
      }

      const vendorRef = ref(db, dbPath);
      const snapshot = await get(vendorRef);

      if (snapshot.exists()) {
        const vendorData = snapshot.val();
        setVendorDetails({ name: vendorData.name, image: vendorData.image || vendorData.imageUrl });
      } else {
        console.error(`Vendor with ID ${vendorId} not found in the database at path: ${dbPath}`);
      }
      setIsLoadingVendor(false);
    };

    // If we don't have the details from location.state, fetch them.
    if (!vendorDetails.name || !vendorDetails.image) {
      fetchVendorDetails();
    } else {
      setIsLoadingVendor(false);
    }
  }, [vendorId, vendorType, navigate, vendorCart, vendorDetails.name, vendorDetails.image]);

  useEffect(() => {
    if (isAddressModalOpen) {
      setIsFetchingLocation(true);
      setCurrentLocationAddress('');
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (window.google && window.google.maps) {
              if (!geocoder.current) geocoder.current = new window.google.maps.Geocoder();
              geocoder.current.geocode(
                { location: { lat: position.coords.latitude, lng: position.coords.longitude } },
                (results, status) => {
                  if (status === "OK" && results[0]) {
                    setCurrentLocationAddress(results[0].formatted_address);
                  } else {
                    setCurrentLocationAddress('Could not determine address.');
                  }
                  setIsFetchingLocation(false);
                }
              );
            }
          },
          (error) => {
            let errorMessage = 'An unknown error occurred.';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Geolocation permission denied.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable.';
                break;
              case error.TIMEOUT:
                errorMessage = 'The request to get user location timed out.';
                break;
              default:
                break;
            }
            setCurrentLocationAddress(errorMessage);
            setIsFetchingLocation(false);
          }
        );
      } else {
        setCurrentLocationAddress('Geolocation not supported.');
        setIsFetchingLocation(false);
      }
    }
  }, [isAddressModalOpen]);

  const handlePlaceOrder = () => {
    // TODO: Implement order placement logic (e.g., save to Firebase, process payment)
    alert('Order placed successfully!');
    if (vendorId) {
      clearVendorCart(vendorId);
    }
    navigate('/food-vendors'); // Redirect after order
  };

  const handleSaveNote = () => {
    setNote(tempNote);
    setIsNoteModalOpen(false);
  };

  const handleBrownBagQuantity = (delta) => {
    setBrownBagQuantity(q => Math.max(0, q + delta));
  };
  const handleOpenGiftModal = () => {
    // Determine the default sender name from the logged-in user when the modal is opened.
    let defaultSenderName = '';
    if (riderData) {
      const userFullName = `${riderData.first_name || ''} ${riderData.last_name || ''}`.trim();
      defaultSenderName = userFullName || riderData.name || '';
    }

    setTempGiftDetails({
      // If a sender name is already set for the gift, use it. Otherwise, use the default.
      senderName: senderName || defaultSenderName,
      recipientName,
      recipientPhone,
      recipientEmail,
      specialMessage,
      saveBeneficiary,
    });
    setIsGiftModalOpen(true);
  };

  const handleGiftDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTempGiftDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveGift = (e) => {
    e.preventDefault();
    setSenderName(tempGiftDetails.senderName);
    setRecipientName(tempGiftDetails.recipientName);
    setRecipientPhone(tempGiftDetails.recipientPhone);
    setRecipientEmail(tempGiftDetails.recipientEmail);
    setSpecialMessage(tempGiftDetails.specialMessage);
    setSaveBeneficiary(tempGiftDetails.saveBeneficiary);
    setIsGift(!!tempGiftDetails.recipientName); // It's a gift if a recipient is named
    setIsGiftModalOpen(false);
  };

  const handleClearGift = () => {
    setIsGift(false);
    setSenderName('');
    setRecipientName('');
    setRecipientPhone('');
    setRecipientEmail('');
    setSpecialMessage('');
    setSaveBeneficiary(false);
    // ... reset other fields if needed
    setIsGiftModalOpen(false);
  };

  const handleNewAddressChange = (e) => {
    const value = e.target.value;
    setNewAddressInput(value);

    if (!value) {
      setPredictions([]);
      return;
    }

    if (window.google && window.google.maps && window.google.maps.places) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      autocompleteService.current.getPlacePredictions({ input: value, componentRestrictions: { country: 'ng' } }, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      });
    }
  };

  const handlePredictionSelect = (prediction) => {
    setDeliveryAddress(prediction.description);
    localStorage.setItem('9troride_address', prediction.description);
    setIsAddressModalOpen(false);
    setNewAddressInput('');
    setPredictions([]);
  };

  const handleUseCurrentLocation = () => {
    if (currentLocationAddress && !isFetchingLocation && !currentLocationAddress.includes('Could not') && !currentLocationAddress.includes('denied') && !currentLocationAddress.includes('unavailable') && !currentLocationAddress.includes('timed out') && !currentLocationAddress.includes('supported')) {
      handlePredictionSelect({ description: currentLocationAddress });
    }
  };

  return (
    <>
      <SEO title="Checkout" description="Complete your order." />
      <FoodNavbar showBackBtn={true} address={address} locationType={locationType} />
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-main">
            <h1>Checkout</h1>

            <div className="checkout-section">
              <h2>Delivery Details</h2>
              <div className="delivery-options-list">
                <button className="delivery-option-item" onClick={() => setIsAddressModalOpen(true)}>
                  <div className="item-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  </div>
                  <div className="item-content">
                    <h2>{deliveryAddress}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                      <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111', lineHeight: '1.4' }}>{deliveryAddress}{deliveryAddress && !deliveryAddress.includes('No saved address') && ' 🇳🇬'}</h2>
                    </div>
                    <div className="item-subtitle">Delivery Address</div>
                  </div>
                  <div className="item-chevron"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></div>
                </button>
                <button className="delivery-option-item" onClick={() => { setTempNote(note); setIsNoteModalOpen(true); }}>
                  <div className="item-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                  </div>
                  <div className="item-content">
                    <h2>{note ? 'Edit note for rider' : 'Leave a note for the rider'}</h2>
                    <div className="item-subtitle">any instruction for a smooth delivery?</div>
                  </div>
                  <div className="item-chevron"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></div>
                </button>
                <button className="delivery-option-item" onClick={handleOpenGiftModal}>
                  <div className="item-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
                  </div>
                  <div className="item-content">
                    <h2>{isGift ? 'This is a gift' : 'Gift this order'}</h2>
                  </div>
                  <div className="item-chevron"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></div>
                </button>
              </div>
            </div>

            <div className="checkout-section">
              <h2>Payment Method</h2>
              <div className="payment-options">
                <label className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
                  <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                  <div className="payment-option-content">
                    <span>Pay with Card</span>
                    <div className="card-icons">
                      {/* Placeholder for card icons */}
                    </div>
                  </div>
                </label>
                <label className={`payment-option ${paymentMethod === 'wallet' ? 'selected' : ''}`}>
                  <input type="radio" name="payment" value="wallet" checked={paymentMethod === 'wallet'} onChange={() => setPaymentMethod('wallet')} />
                  <div className="payment-option-content">
                    <span>Pay with Wallet</span>
                    <span className="wallet-balance">Balance: ₦0.00</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="checkout-sidebar">
            <div className="order-summary">
              <h2>Order Summary</h2>
              {isLoadingVendor ? (
                <div className="vendor-summary-loading">Loading vendor...</div>
              ) : (
                vendorDetails.name && vendorDetails.image && (
                <div className="vendor-summary-header">
                  <img src={vendorDetails.image} alt={`Logo for ${vendorDetails.name}`} className="vendor-summary-image" />
                  <div className="vendor-summary-details">
                    <div className="vendor-summary-name">{vendorDetails.name}</div>
                    {vendorCart.length > 0 && (
                      <div className="vendor-item-count">
                        {totalItemsInCart} {totalItemsInCart > 1 ? 'items' : 'item'}
                      </div>
                    )}
                  </div>
                  <button
                    className="edit-order-btn"
                    onClick={() => {
                      // Navigate to the correct details page based on type
                      const path = vendorType === 'chef' ? `/9troeat/chef/${vendorId}` : vendorType === 'store' ? `/9troeat/store/${vendorId}` : `/9troeat/restaurant/${vendorId}`;
                      navigate(path, { state: { vendor: vendorCart[0]?.vendor, address, locationType } });
                    }}
                  >
                    Edit
                  </button>
                </div>
                )
              )}
              <div className="order-items">
                {vendorCart.map((item, index) => (
                  <div key={index} className="order-item">
                    <img src={item.image} alt={item.name} className="order-item-image" />
                    <div className="order-item-text-content"> {/* This div now contains name and price */}
                      <span className="order-item-name">{item.name}</span>
                      <span className="order-item-price">₦{(item.totalNumericPrice * item.quantity).toLocaleString()}</span>
                    </div>
                    <span className="order-item-quantity-badge">{item.quantity}x</span>
                  </div>
                ))}
              </div>
              <div className="brown-bag-section">
                <div className="brown-bag-header" onClick={() => setIsBrownBagOpen(!isBrownBagOpen)}>
                  <div className="brown-bag-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                  </div>
                  <div className="brown-bag-text">
                    <h3>Need a brown bag?</h3>
                    <p>Package your order in our custom brown bag for just ₦200</p>
                  </div>
                  <div className={`brown-bag-chevron ${isBrownBagOpen ? 'open' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  </div>
                </div>
                {isBrownBagOpen && (
                  <div className="brown-bag-details">
                    <div className="brown-bag-item">
                      <div className="item-info">
                        <h4>9troeat Bag</h4>
                        <p>₦{brownBagPrice.toLocaleString()}</p>
                      </div>
                      <div className="item-controls">
                        <button onClick={() => handleBrownBagQuantity(-1)} disabled={brownBagQuantity === 0}>-</button>
                        <span>{brownBagQuantity}</span>
                        <button onClick={() => handleBrownBagQuantity(1)}>+</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>₦{cartTotal.toLocaleString()}</span>
                </div>
                <div className="total-row">
                  <span>Delivery Fee</span>
                  <span>₦{deliveryFee.toLocaleString()}</span>
                </div>
                {brownBagQuantity > 0 && (
                  <div className="total-row">
                    <span>Bag</span>
                    <span>₦{brownBagTotal.toLocaleString()}</span>
                  </div>
                )}
                <div className="total-row">
                  <span>Service Fee</span>
                  <span>₦{serviceFee.toLocaleString()}</span>
                </div>
                <div className="total-row grand-total">
                  <span>Total</span>
                  <span>₦{total.toLocaleString()}</span>
                </div>
              </div>
              <button className="place-order-btn" onClick={handlePlaceOrder} disabled={vendorCart.length === 0}>
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>

      {isAddressModalOpen && (
        <div className="checkout-modal-overlay" onClick={() => setIsAddressModalOpen(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-modal-header">
              <h2>Delivery Address</h2>
              <button className="checkout-modal-close-btn" onClick={() => setIsAddressModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="address-modal-content">
              <div className="address-search-input-wrapper">
                <div className="address-search-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                </div>
                <input
                  type="text"
                  className="address-search-input"
                  placeholder="Enter new address"
                  value={newAddressInput}
                  onChange={handleNewAddressChange}
                />
                {newAddressInput && (
                  <button className="clear-address-btn" onClick={() => setNewAddressInput('')}>×</button>
                )}
              </div>
              {predictions.length > 0 && (
                <ul className="address-predictions-list">
                  {predictions.map((p) => (
                    <li key={p.place_id} onClick={() => handlePredictionSelect(p)}>
                      {p.description}
                    </li>
                  ))}
                </ul>
              )}
              <button className="delivery-option-item current-location-option" onClick={handleUseCurrentLocation} disabled={isFetchingLocation || !currentLocationAddress || currentLocationAddress.includes('Could not') || currentLocationAddress.includes('denied') || currentLocationAddress.includes('unavailable') || currentLocationAddress.includes('timed out') || currentLocationAddress.includes('supported')}>
                <div className="item-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"/><path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"/></svg></div>
                <div className="item-content">
                  <div className="item-title">Use current location</div>
                  {isFetchingLocation ? (
                    <div className="item-subtitle">Fetching address...</div>
                  ) : (
                    currentLocationAddress && <div className="item-subtitle">{currentLocationAddress}</div>
                  )}
                </div>
              </button>
              <div className="saved-addresses-section">
                <h3 className="saved-addresses-title">Saved Addresses</h3>
                <div className="saved-addresses">
                  {riderData?.savedLocations?.home && (
                    <button className="delivery-option-item" onClick={() => handlePredictionSelect({ description: riderData.savedLocations.home.address })}>
                      <div className="item-icon"><svg width="20" height="20" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path><polyline points="9 22 9 12 15 12 15 22" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></polyline></svg></div>
                      <div className="item-content">{riderData.savedLocations.home.address}</div>
                    </button>
                  )}
                  {riderData?.savedLocations?.work && (
                    <button className="delivery-option-item" onClick={() => handlePredictionSelect({ description: riderData.savedLocations.work.address })}>
                      <div className="item-icon"><svg width="20" height="20" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg></div>
                      <div className="item-content">{riderData.savedLocations.work.address}</div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isNoteModalOpen && (
        <div className="checkout-modal-overlay" onClick={() => setIsNoteModalOpen(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-modal-header">
              <h2>Leave a note for the rider</h2>
              <button className="checkout-modal-close-btn" onClick={() => setIsNoteModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="checkout-modal-body">
              <textarea
                className="checkout-modal-textarea"
                defaultValue={note}
                placeholder="e.g. Please call upon arrival."
                onChange={(e) => setTempNote(e.target.value)}
              />
            </div>
            <div className="checkout-modal-footer">
              <button className="checkout-modal-btn" onClick={handleSaveNote}>Save Note</button>
            </div>
          </div>
        </div>
      )}

      {isGiftModalOpen && (
        <div className="checkout-modal-overlay" onClick={() => setIsGiftModalOpen(false)}>
          <div className="checkout-modal checkout-modal--gift" onClick={(e) => e.stopPropagation()}>
            <GiftOrderForm
              giftDetails={tempGiftDetails}
              deliveryAddress={deliveryAddress}
              onChange={handleGiftDetailsChange}
              onSave={handleSaveGift}
              onClear={handleClearGift}
              onClose={() => setIsGiftModalOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Checkout;